import { useMemo, useState } from 'react'

import { useSession } from '@/features/auth/api/useSession'

/* Everything a user has done to a list surface that is not part of the data:
 * which view they left it in, which columns they turned off, how they sorted
 * it, what they narrowed it to (UX-DR-4).
 *
 * IT LIVES IN localStorage, NOT ON THE SERVER, and that is a decision rather
 * than an omission. The retired `useListView` — the per-page hook this
 * generalises and replaces — already made the argument for the table/board
 * choice: these are a workstation habit, not a profile setting. Filters, hidden columns and sort are the same kind of
 * fact. A user who narrows the case list to "in transit" on the terminal by
 * the loading bay does not thereby want their laptop narrowed too. If a
 * profile-level preferences service is ever wanted it is a backend story with
 * its own migration, and this key shape is what it would import; there is no
 * general preferences endpoint today and this is not the issue that adds one.
 *
 *   asa:view:<userId>:<surface>:<facet>
 *
 * THE USER ID IS IN THE KEY even though the storage is already per device.
 * Two people share the workstation in the Türkmenbaşy office; without the id
 * the second one inherits the first one's filters and sees a list narrowed by
 * a colleague — a small leak of what that colleague is looking at, and free to
 * avoid. While the session is still in flight the id is `anon`, and the entry
 * is re-read once the real id arrives, so the first paint never writes one
 * user's habits under another's name.
 *
 * THE FACETS ARE ONE ENTRY EACH, not one record under one key. That is what
 * makes the key shape above literally true, and it means a single corrupt
 * facet costs the user that facet rather than the whole screen.
 *
 * EVERY READ SURVIVES GARBAGE. localStorage is user-writable, it outlives the
 * code that wrote it, and a column id that was renamed last release is still
 * sitting in it. A parse that throws here happens during render and blanks the
 * whole list, so each read validates the shape it got and falls back to the
 * default rather than trusting what it found.
 */

export type ListView = 'table' | 'board'

export interface ListViewState {
  view: ListView
  /** Column ids the user turned OFF on this surface. */
  hiddenColumns: string[]
  /** TanStack `SortingState`, persisted. */
  sorting: { id: string; desc: boolean }[]
  /** Facet id → selected values. `[]` or absent means no narrowing. */
  filters: Record<string, string[]>
  /** Rows per page on the table half. A habit like the rest: the person who
   *  works from a 13" laptop wants 25 and the one at the desk wants 100, and
   *  neither wants to say so again tomorrow. */
  pageSize: number
}

export interface ListViewControls {
  setView(next: ListView): void
  setHiddenColumns(ids: string[]): void
  toggleColumn(id: string): void
  setSorting(next: { id: string; desc: boolean }[]): void
  setFilter(facet: string, values: string[]): void
  clearFilters(): void
  setPageSize(next: number): void
}

/** The entries one surface owns. Named rather than inlined so the key builder
 *  and the reader cannot drift apart. */
type Facet = 'view' | 'columns' | 'sort' | 'filters' | 'pageSize'

/** The id used until the session answers. Not a real user id, and deliberately
 *  not a reserved-looking one — an entry under `anon` is a legitimate thing to
 *  find in storage on a machine nobody has signed in on yet. */
const ANON = 'anon'

/** The page sizes a table may offer, and the one it starts at.
 *
 *  A CLOSED SET, and the read below rejects anything outside it. The value ends
 *  up in a row model, so a stored `0` from a hand-edited store is an empty table
 *  and a stored `100000` is the frozen tab this threshold exists to prevent. */
export const PAGE_SIZES: readonly number[] = [25, 50, 100]

const DEFAULTS: ListViewState = {
  view: 'table',
  hiddenColumns: [],
  sorting: [],
  filters: {},
  pageSize: 25,
}

/** `asa:view:<userId>:<surface>:<facet>` — the shape decided for this issue. */
export function viewStateKey(userId: string, surface: string, facet: Facet): string {
  return `asa:view:${userId}:${surface}:${facet}`
}

/* Every value is stored as JSON, including `view`, whose payload is a bare
 * string. Uniformity buys one reader and one writer for all four facets; the
 * alternative — a raw string for the scalar and JSON for the rest — is two
 * code paths that have to agree about which facet is which, and that is the
 * kind of agreement that stops holding. `"board"` is still legible to anyone
 * reading storage in devtools. */
function read<T>(key: string, validate: (raw: unknown) => T | null, fallback: T): T {
  let stored: string | null
  try {
    stored = localStorage.getItem(key)
  } catch {
    // Storage disabled entirely — Safari private browsing, a locked-down
    // policy. The surface still works, it just cannot remember anything.
    return fallback
  }
  if (stored === null) return fallback
  try {
    const validated = validate(JSON.parse(stored))
    return validated ?? fallback
  } catch {
    return fallback
  }
}

function write(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // A full quota or a blocked store must not take the interaction with it:
    // the user's click still changes the view, it just is not remembered.
  }
}

function asView(raw: unknown): ListView | null {
  return raw === 'table' || raw === 'board' ? raw : null
}

function asColumnIds(raw: unknown): string[] | null {
  return Array.isArray(raw) && raw.every((id) => typeof id === 'string') ? raw : null
}

function asSorting(raw: unknown): { id: string; desc: boolean }[] | null {
  if (!Array.isArray(raw)) return null
  const ok = raw.every(
    (entry) =>
      typeof entry === 'object' &&
      entry !== null &&
      typeof (entry as { id?: unknown }).id === 'string' &&
      typeof (entry as { desc?: unknown }).desc === 'boolean',
  )
  return ok ? (raw as { id: string; desc: boolean }[]) : null
}

function asFilters(raw: unknown): Record<string, string[]> | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null
  const entries = Object.entries(raw as Record<string, unknown>)
  const ok = entries.every(
    ([, values]) =>
      Array.isArray(values) && values.every((value) => typeof value === 'string'),
  )
  return ok ? (raw as Record<string, string[]>) : null
}

function asPageSize(raw: unknown): number | null {
  return typeof raw === 'number' && PAGE_SIZES.includes(raw) ? raw : null
}

function readState(userId: string, surface: string): ListViewState {
  return {
    view: read(viewStateKey(userId, surface, 'view'), asView, DEFAULTS.view),
    hiddenColumns: read(
      viewStateKey(userId, surface, 'columns'),
      asColumnIds,
      DEFAULTS.hiddenColumns,
    ),
    sorting: read(viewStateKey(userId, surface, 'sort'), asSorting, DEFAULTS.sorting),
    filters: read(
      viewStateKey(userId, surface, 'filters'),
      asFilters,
      DEFAULTS.filters,
    ),
    pageSize: read(
      viewStateKey(userId, surface, 'pageSize'),
      asPageSize,
      DEFAULTS.pageSize,
    ),
  }
}

/**
 * The persisted view state of one list surface, plus the setters that change
 * it. `surface` is a stable per-screen slug — 'quotes', 'cases' — and is part
 * of the key, so two surfaces never share a filter.
 *
 * Returns the state and the controls as a tuple, the shape the per-page hook
 * this replaces already had, so a page migrates by changing one line.
 */
export function useViewState(surface: string): readonly [ListViewState, ListViewControls] {
  const { data: user } = useSession()
  const userId = user?.id ?? ANON

  const [state, setState] = useState<ListViewState>(() => readState(userId, surface))

  /* Re-read when the identity of the entry changes — the session resolving
     from `anon` to a real id, or a page reusing this hook for a different
     surface. Done DURING RENDER rather than in an effect, the pattern
     `useFormDraft` and `DatePicker` document and `react-hooks/set-state-in-
     effect` requires: an effect would paint one frame of the previous user's
     filters before correcting them, and a filtered list is exactly the frame
     that must not be shown to the wrong person. */
  /* Joined on U+0000, written as an ESCAPE rather than typed literally: a raw
     NUL byte in the source makes git treat this file as binary and stop diffing
     it, which costs every later reviewer their view of this hook. The separator
     itself is deliberate — it is the one character that cannot occur in a uuid
     or in a surface slug, so no two pairs can collide. */
  const identity = `${userId}\u0000${surface}`
  const [lastIdentity, setLastIdentity] = useState(identity)
  if (identity !== lastIdentity) {
    setLastIdentity(identity)
    setState(readState(userId, surface))
  }

  /* Every setter writes storage and state together, and derives the next value
     from the previous STATE rather than from a re-read: two controls changed in
     one tick — clearing a filter while a column toggle is still queued — must
     not have the second one read a store the first has not flushed yet. */
  const controls = useMemo<ListViewControls>(() => {
    const put = <K extends keyof ListViewState>(
      facet: Facet,
      key: K,
      next: (prev: ListViewState) => ListViewState[K],
    ) =>
      setState((prev) => {
        const value = next(prev)
        write(viewStateKey(userId, surface, facet), value)
        return { ...prev, [key]: value }
      })

    return {
      setView: (next) => put('view', 'view', () => next),
      setHiddenColumns: (ids) => put('columns', 'hiddenColumns', () => ids),
      toggleColumn: (id) =>
        put('columns', 'hiddenColumns', (prev) =>
          prev.hiddenColumns.includes(id)
            ? prev.hiddenColumns.filter((held) => held !== id)
            : [...prev.hiddenColumns, id],
        ),
      setSorting: (next) => put('sort', 'sorting', () => next),
      setFilter: (facet, values) =>
        put('filters', 'filters', (prev) => {
          const next = { ...prev.filters }
          // An empty selection is DELETED rather than stored as []: "no
          // narrowing" and "narrowed to nothing" must not be the same entry,
          // and the count of active filters is read off this object's keys.
          if (values.length === 0) delete next[facet]
          else next[facet] = values
          return next
        }),
      clearFilters: () => put('filters', 'filters', () => ({})),
      setPageSize: (next) => put('pageSize', 'pageSize', () => next),
    }
  }, [userId, surface])

  return [state, controls] as const
}

/* ── Column visibility, in the two shapes that need it ─────────────────────
 * The hook stores what the user turned OFF, because that is the short list and
 * the one that survives a column being added: a new column a user has never
 * seen is visible, which is the right default. TanStack's `VisibilityState`
 * is the opposite — a map of what is on — so the translation lives here, once,
 * rather than being re-derived by every page that wires the two together. */

/** Hidden ids → the `columnVisibility` state DataTable takes. */
export function toVisibilityState(hiddenColumns: string[]): Record<string, boolean> {
  return Object.fromEntries(hiddenColumns.map((id) => [id, false]))
}

/** A `columnVisibility` map back to the hidden ids the hook stores. */
export function toHiddenColumns(visibility: Record<string, boolean>): string[] {
  return Object.entries(visibility)
    .filter(([, visible]) => !visible)
    .map(([id]) => id)
}
